import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Shield, MailCheck, ArrowLeft } from 'lucide-react';

const FOREST = '#2C4A3B';
const FOREST_DARK = '#1E3329';
const TERRA = '#C47A5E';
const CREAM = '#F9F6F0';
const CHARCOAL = '#1C1917';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Backend always returns 200 to prevent email enumeration —
      // we show the same success screen whether or not the account exists.
      await axios.post(`${API}/auth/password-reset/request`, { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ fontFamily: "'Manrope', sans-serif", background: CREAM }}
    >
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-[0.06]" style={{ background: FOREST }} aria-hidden="true" />
      <div className="absolute -bottom-32 -left-20 w-80 h-80 rounded-full opacity-[0.08]" style={{ background: TERRA }} aria-hidden="true" />

      <div className="w-full max-w-md relative">
        <div className="rounded-3xl shadow-xl p-8 sm:p-10 border" style={{ background: '#fff', borderColor: '#E3E8E4' }}>
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-md" style={{ background: sent ? TERRA : FOREST }}>
              {sent
                ? <MailCheck className="w-8 h-8" style={{ color: CREAM }} />
                : <Shield className="w-8 h-8" style={{ color: CREAM }} />}
            </div>
          </div>

          {sent ? (
            <>
              <h1 className="text-2xl sm:text-3xl font-bold text-center mb-3 tracking-tight" style={{ color: CHARCOAL }}>
                Check your inbox
              </h1>
              <p className="text-center text-sm mb-6 leading-relaxed" style={{ color: '#6B8E7B' }}>
                If <strong style={{ color: CHARCOAL }}>{email}</strong> is registered, we've sent a password-reset link that expires in 60 minutes.
                <br /><br />
                Don't see the email? Check your spam folder, or wait a minute and try again.
              </p>
              <Link
                to="/login"
                data-testid="back-to-login-link"
                className="flex items-center justify-center gap-2 w-full h-11 rounded-xl font-semibold transition-all hover:opacity-90"
                style={{ background: FOREST, color: '#fff' }}
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Sign In
              </Link>
            </>
          ) : (
            <>
              <h1 className="text-2xl sm:text-3xl font-bold text-center mb-2 tracking-tight" style={{ color: CHARCOAL }}>
                Reset your password
              </h1>
              <p className="text-center text-sm mb-8" style={{ color: '#6B8E7B' }}>
                Enter the email you registered with and we'll send you a secure reset link.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="forgot-password-form">
                <div>
                  <Label htmlFor="email" className="text-sm font-medium" style={{ color: CHARCOAL }}>
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="provider@example.com"
                    required
                    autoFocus
                    data-testid="forgot-password-email-input"
                    className="mt-1.5 h-11 rounded-xl focus:outline-none focus:ring-2"
                    style={{ borderColor: '#E3E8E4' }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = FOREST; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = '#E3E8E4'; }}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading || !email}
                  data-testid="forgot-password-submit"
                  className="w-full h-11 rounded-xl text-white font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-60"
                  style={{ background: loading ? FOREST_DARK : FOREST }}
                >
                  {loading ? 'Sending link…' : 'Send Reset Link'}
                </Button>
              </form>

              <p className="text-center text-sm mt-6" style={{ color: '#6B8E7B' }}>
                Remembered it?{' '}
                <Link to="/login" className="font-semibold hover:underline" style={{ color: TERRA }} data-testid="remembered-login-link">
                  Sign in
                </Link>
              </p>
            </>
          )}
        </div>
        <p className="text-center text-xs mt-6" style={{ color: '#6B8E7B' }}>
          © Plexura AI Scribe · HIPAA-compliant mental health scribing
        </p>
      </div>
    </div>
  );
}
